import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function VancouverPizzaGuide() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "vancouvers-must-try-pizza";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Via Tevere Pizzeria Napoletana",
    "section-2": "Nicli Antica Pizzeria",
    "section-3": "AJ’s Brooklyn Pizza Joint",
    "section-4": "Virtuous Pie",
    "section-5": "The Parlour",
    "section-6": "Pizza Carano",
    "section-7": "Straight Outta Brooklyn NYC Pizzeria",
    "section-8": "Uncle Fatih’s Pizza",
    "section-9": "Slice of Chicago Deep Dish Pizza",
    "section-10": "Pizzeria Farina",
    faq: "FAQ",
    "local-regulations": "Local Regulations",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.vch.ca/",
      label: "Vancouver Coastal Health – Food Safety & Inspections",
    },
    {
      url: "https://www.vanmag.com/",
      label: "Vancouver Magazine – Food & Drink",
    },
    {
      url: "https://www.viatevere.ca/",
      label: "Via Tevere Pizzeria (Official Site)",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  const locations = [
    {
      id: "section-1",
      lat: 49.2765,
      lng: -123.0659,
      popupText: "Via Tevere Pizzeria Napoletana",
    },
    {
      id: "section-2",
      lat: 49.2543,
      lng: -123.2345,
      popupText: "Nicli Antica Pizzeria",
    },
    {
      id: "section-3",
      lat: 49.2628,
      lng: -123.0980,
      popupText: "AJ’s Brooklyn Pizza Joint",
    },
    {
      id: "section-4",
      lat: 49.2797,
      lng: -123.1000,
      popupText: "Virtuous Pie",
    },
    {
      id: "section-5",
      lat: 49.2766,
      lng: -123.1217,
      popupText: "The Parlour",
    },
    {
      id: "section-6",
      lat: 49.2489,
      lng: -123.0907,
      popupText: "Pizza Carano",
    },
    {
      id: "section-7",
      lat: 49.2800,
      lng: -123.1155,
      popupText: "Straight Outta Brooklyn NYC Pizzeria (Downtown)",
    },
    {
      id: "section-8",
      lat: 49.2624,
      lng: -123.0693,
      popupText: "Uncle Fatih’s Pizza (Commercial-Broadway)",
    },
    {
      id: "section-9",
      lat: 49.2327,
      lng: -123.0903,
      popupText: "Slice of Chicago Deep Dish Pizza",
    },
    {
      id: "section-10",
      lat: 49.2775,
      lng: -123.0961,
      popupText: "Pizzeria Farina",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Vancouver’s Must-Try Pizza</title>
        <meta
          name="description"
          content="Discover Vancouver's must-try pizza spots, from Neapolitan to New York style, with helpful tips on local regulations and FAQs."
        />
        <meta
          name="keywords"
          content="pizza, Vancouver, food, dining, restaurants"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/vancouvers-must-try-pizza.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Vancouver’s Must-Try Pizza"
        />
        <meta
          property="og:description"
          content="Explore a comprehensive guide to Vancouver's best pizza spots."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/vancouvers-must-try-pizza.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/vancouvers-must-try-pizza.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Vancouver’s Must-Try Pizza"
        />
        <meta
          name="twitter:description"
          content="A practical, in-depth guide to the city's top pizza restaurants."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/vancouvers-must-try-pizza.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={ref_data?.image || "https://bcouver.com/assets/images/article-fallback.jpg"}
              alt="Delicious pizza from Vancouver"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia/>

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver’s pizza scene is as diverse as the city itself. From blistered
              Neapolitan pies to giant New York slices, there’s something for every pizza
              lover. In this guide, we’ll showcase 10 must-try pizzerias across different
              styles and budgets—covering everything from vegan options to Chicago-style
              deep dish. Whether you’re a local seeking your next pizza fix or a visitor
              exploring Vancouver’s culinary landscape, this guide has you covered.
            </p>

            {/* SECTION: Via Tevere */}
            <h2 id="section-1">Via Tevere Pizzeria Napoletana</h2>
            <p ref={(el) => setSpotRef("section-1", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2765}
                coordX={-123.0659}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (Latitude: 49.2765, Longitude: -123.0659)
            </p>
            <p>
              An East Van institution for authentic Neapolitan pizza, Via Tevere
              serves pies cooked in a wood-fired oven. Family-run and named after
              the owners’ father’s street in Naples, the restaurant offers a lively
              atmosphere, classic favorites like Margherita and Quattro Formaggi, and
              a perfectly charred crust. Expect lines at peak hours, as they do not
              take reservations.
            </p>
            <ul>
              <li><strong>Address:</strong> 1190 Victoria Dr, Vancouver, BC V5L 4G5</li>
              <li><strong>Hours:</strong> Sun–Thu 4:00pm–9:30pm; Fri–Sat 4:00pm–10:30pm</li>
              <li><strong>Approx. Cost:</strong> $15–$20 per pizza</li>
              <li><strong>Dietary:</strong> Vegetarian options, vegan Marinara pizza, no gluten-free crust</li>
              <li><strong>Services:</strong> Dine-in, Takeout, Delivery via apps</li>
            </ul>

            {/* SECTION: Nicli Antica */}
            <h2 id="section-2">Nicli Antica Pizzeria</h2>
            <p ref={(el) => setSpotRef("section-2", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2543}
                coordX={-123.2345}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (Latitude: 49.2543, Longitude: -123.2345)
            </p>
            <p>
              Vancouver’s first pizzeria to earn Vera Pizza Napoletana status, Nicli
              Antica has relocated to Wesbrook Village at UBC. Known for using San
              Marzano tomatoes and fresh fior di latte, they also offer creative specials
              like bacon jam-topped pizzas. With a modern Italian vibe and a great cocktail
              menu, Nicli is ideal for an upscale pizza night.
            </p>
            <ul>
              <li><strong>Address:</strong> 3339 Shrum Lane, Vancouver, BC V6S 0B9</li>
              <li><strong>Hours:</strong> Daily 11:30am–9:00pm</li>
              <li><strong>Approx. Cost:</strong> $16–$22 per pizza</li>
              <li><strong>Dietary:</strong> Vegetarian, vegan cheese available, gluten-free crust available</li>
              <li><strong>Services:</strong> Dine-in, Takeout, Delivery</li>
            </ul>

            {/* SECTION: AJ’s Brooklyn */}
            <h2 id="section-3">AJ’s Brooklyn Pizza Joint</h2>
            <p ref={(el) => setSpotRef("section-3", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2628}
                coordX={-123.0980}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (Latitude: 49.2628, Longitude: -123.0980)
            </p>
            <p>
              For a classic New York slice experience, head to AJ’s on East Broadway.
              Expect 18-inch foldable pies or thick-crust Detroit-style squares with
              caramelized edges. It’s a laid-back spot for anyone craving a taste of
              Brooklyn, and a favorite among locals seeking big, satisfying slices.
            </p>
            <ul>
              <li><strong>Address:</strong> 325 E Broadway, Vancouver, BC V5T 1W5</li>
              <li><strong>Hours:</strong> Mon–Thu 11:00am–10:00pm; Fri–Sat 11:00am–11:00pm; Sun 12:00pm–9:00pm</li>
              <li><strong>Approx. Cost:</strong> $4–$5 per slice; $20–$30 per whole pie</li>
              <li><strong>Dietary:</strong> Vegetarian options, limited vegan, no gluten-free</li>
              <li><strong>Services:</strong> Dine-in, Takeout, Delivery</li>
            </ul>

            {/* SECTION: Virtuous Pie */}
            <h2 id="section-4">Virtuous Pie</h2>
            <p ref={(el) => setSpotRef("section-4", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2797}
                coordX={-123.1000}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (Latitude: 49.2797, Longitude: -123.1000)
            </p>
            <p>
              A 100% plant-based pizzeria, Virtuous Pie is a must for vegans and
              omnivores alike. Their personal-sized pies feature inventive toppings
              like BBQ jackfruit, cashew mozzarella, and pesto drizzles. Don’t miss
              their house-made vegan ice cream for dessert.
            </p>
            <ul>
              <li><strong>Address:</strong> 583 Main St, Vancouver, BC V6A 2V1</li>
              <li><strong>Hours:</strong> Daily 11:30am–10:00pm</li>
              <li><strong>Approx. Cost:</strong> $12–$16 per personal pizza</li>
              <li><strong>Dietary:</strong> 100% vegan menu, gluten-free crust available</li>
              <li><strong>Services:</strong> Dine-in, Takeout, Delivery</li>
            </ul>

            {/* SECTION: The Parlour */}
            <h2 id="section-5">The Parlour</h2>
            <p ref={(el) => setSpotRef("section-5", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2766}
                coordX={-123.1217}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (Latitude: 49.2766, Longitude: -123.1217)
            </p>
            <p>
              Located in Yaletown, The Parlour is renowned for its creative gourmet
              pizzas in a trendy lounge atmosphere. Expect fun topping combos—fig jam,
              truffle oil, prosciutto—and a lively setting with craft cocktails and
              late-night hours. Ideal for a date night or an outing with friends.
            </p>
            <ul>
              <li><strong>Address:</strong> 1011 Hamilton St, Vancouver, BC V6B 5T4</li>
              <li><strong>Hours:</strong> Vary by day (open till late, check website)</li>
              <li><strong>Approx. Cost:</strong> $18–$25 per pizza</li>
              <li><strong>Dietary:</strong> Vegetarian, vegan cheese on request, gluten-free crust available</li>
              <li><strong>Services:</strong> Dine-in, Takeout, Delivery</li>
            </ul>

            {/* SECTION: Pizza Carano */}
            <h2 id="section-6">Pizza Carano</h2>
            <p ref={(el) => setSpotRef("section-6", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2489}
                coordX={-123.0907}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (Latitude: 49.2489, Longitude: -123.0907)
            </p>
            <p>
              This cozy Fraserhood gem is celebrated for its inventive flavor
              combinations (like the Spicy Coconut Kale pizza) and a thin,
              slightly charred crust. With a small dining room and no reservations,
              Pizza Carano is best visited early or on weeknights to avoid long waits.
            </p>
            <ul>
              <li><strong>Address:</strong> 4241 Fraser St, Vancouver, BC V5V 4G1</li>
              <li><strong>Hours:</strong> Mon–Wed 5pm–10pm; Thu–Sat 5pm–Late; Sun 5pm–9pm; closed Tuesdays</li>
              <li><strong>Approx. Cost:</strong> $17–$22 per pizza</li>
              <li><strong>Dietary:</strong> Vegetarian options, limited vegan, no gluten-free</li>
              <li><strong>Services:</strong> Dine-in only (takeout limited), Delivery via apps</li>
            </ul>

            {/* SECTION: Straight Outta Brooklyn */}
            <h2 id="section-7">Straight Outta Brooklyn NYC Pizzeria</h2>
            <p ref={(el) => setSpotRef("section-7", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2800}
                coordX={-123.1155}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (Latitude: 49.2800, Longitude: -123.1155)
            </p>
            <p>
              With multiple locations across Vancouver, Straight Outta Brooklyn
              serves classic New York–style slices: big, foldable, and dripping
              with flavor. The downtown spot on Robson is perfect for a quick, budget-friendly
              lunch or a late-night slice.
            </p>
            <ul>
              <li><strong>Address:</strong> 350 Robson St, Vancouver, BC V6B 2B2 (Downtown)</li>
              <li><strong>Hours:</strong> 11am–10pm (Sun–Thu), open later Fri–Sat</li>
              <li><strong>Approx. Cost:</strong> $3.50–$4 per slice, ~$25 for an 18" pie</li>
              <li><strong>Dietary:</strong> Vegetarian slices, limited vegan, no gluten-free</li>
              <li><strong>Services:</strong> Quick dine-in, Takeout, Delivery</li>
            </ul>

            {/* SECTION: Uncle Fatih’s */}
            <h2 id="section-8">Uncle Fatih’s Pizza</h2>
            <p ref={(el) => setSpotRef("section-8", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2624}
                coordX={-123.0693}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (Latitude: 49.2624, Longitude: -123.0693)
            </p>
            <p>
              A local legend for cheap and tasty pizza, Uncle Fatih’s offers a wide
              variety of toppings—from pepperoni to potato-and-corn combos. Ideal for
              students and late-night revelers, it has multiple branches and stays open
              late.
            </p>
            <ul>
              <li><strong>Address:</strong> 1685 E Broadway, Vancouver, BC V5N 1V9 (Flagship)</li>
              <li><strong>Hours:</strong> 10am–1:30am (Sun–Thu), 10am–2:30am (Fri–Sat)</li>
              <li><strong>Approx. Cost:</strong> $2.50–$3.50 per slice, ~$12–$18 per large pie</li>
              <li><strong>Dietary:</strong> Vegetarian slices, no vegan cheese, no gluten-free</li>
              <li><strong>Services:</strong> Mostly takeout, limited dine-in, some delivery</li>
            </ul>

            {/* SECTION: Slice of Chicago */}
            <h2 id="section-9">Slice of Chicago Deep Dish Pizza</h2>
            <p ref={(el) => setSpotRef("section-9", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2327}
                coordX={-123.0903}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (Latitude: 49.2327, Longitude: -123.0903)
            </p>
            <p>
              Craving Chicago-style pizza in Vancouver? Slice of Chicago serves a tall,
              buttery crust loaded with cheese and chunky tomato sauce on top. One small
              pie can feed two, so come hungry or order for takeout. Expect a 30+ minute
              bake time—deep-dish isn’t fast, but it’s worth the wait.
            </p>
            <ul>
              <li><strong>Address:</strong> 5812 Fraser St, Vancouver, BC V5W 2Z5</li>
              <li><strong>Hours:</strong> Mon–Sat 11am–10:30pm; Sun 4pm–10:30pm</li>
              <li><strong>Approx. Cost:</strong> $18–$30+ depending on size/toppings</li>
              <li><strong>Dietary:</strong> Vegetarian options, no vegan cheese, no gluten-free</li>
              <li><strong>Services:</strong> Limited dine-in, Takeout, Delivery</li>
            </ul>

            {/* SECTION: Pizzeria Farina */}
            <h2 id="section-10">Pizzeria Farina</h2>
            <p ref={(el) => setSpotRef("section-10", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2775}
                coordX={-123.0961}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              (Latitude: 49.2775, Longitude: -123.0961)
            </p>
            <p>
              A small, hip pizzeria near Chinatown, Farina serves pies that straddle the
              line between Neapolitan and Roman styles. The dough ferments for 72 hours,
              yielding exceptional flavor and texture. They make a limited quantity
              daily—once they sell out, they close.
            </p>
            <ul>
              <li><strong>Address:</strong> 915 Main St, Vancouver, BC V6A 2V8</li>
              <li><strong>Hours:</strong> 5pm–9:30pm (or until dough runs out), daily</li>
              <li><strong>Approx. Cost:</strong> $15–$18 per pizza</li>
              <li><strong>Dietary:</strong> Vegetarian options, Marinara (vegan), no gluten-free</li>
              <li><strong>Services:</strong> Dine-in (limited seating), Takeout, No formal delivery</li>
            </ul>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Do I need reservations?</h3>
              <p>
                It depends on the spot. Many pizzerias (Via Tevere, Farina, Pizza Carano)
                are walk-in only. Others (The Parlour, Nicli) do accept reservations, but
                it’s best to check their websites for the latest policies.
              </p>

              <h3>2. Are there vegetarian, vegan, or gluten-free options?</h3>
              <p>
                Absolutely. Virtuous Pie is fully vegan, Nicli and The Parlour offer
                gluten-free crust, and all spots have vegetarian pizzas. Check each
                restaurant’s details for specifics.
              </p>

              <h3>3. Where can I find a budget-friendly slice?</h3>
              <p>
                Straight Outta Brooklyn and Uncle Fatih’s are go-to places for big slices
                under $4. They’re quick, no-frills, and perfect for a cheap lunch or
                late-night snack.
              </p>
            </div>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              Vancouver’s restaurants follow strict health and safety standards enforced
              by Vancouver Coastal Health. Diners can view inspection reports online for
              transparency. While there are currently no special restrictions on indoor
              dining, always check the latest official resources if any health or safety
              guidelines change.
            </p>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              From Neapolitan pies to deep-dish decadence, Vancouver’s pizza scene caters
              to every preference and budget. Grab a slice on the go or settle in for a
              gourmet experience; either way, you’ll discover why pizza is such a beloved
              staple in this vibrant city. Now that you’ve got the insider scoop, it’s
              time to plan your own pizza crawl—enjoy!
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom
              referencesData={referencesData}
              ref_data={ref_data}
            />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}